/**
 * Relevo entre el logo reveal de carga y la aplicación (adaptado de src/utils/loginSplash.js de zafiro-web-client,
 * rama IDB-94, sin la co-marca de cliente).
 *
 * El reveal (marcado, estilos y arranque) vive en línea en `public/index.html` para verse antes de que llegue el
 * bundle. Ese arranque decide si se muestra (cargas que terminan en el login) y deja su estado en
 * `window.__zafiroSplash`. Desde acá la app:
 * - toma el relevo al arrancar (`holdLoginSplash`),
 * - lo retira cuando el login está montado (`revealLoginSplash`): el lockup viaja hasta su copia en el encabezado
 *   del login (`getLoginLockup`), que queda en su lugar.
 *
 * Todas las funciones son inocuas si el reveal no se está mostrando.
 */

const SPLASH_ELEMENT_ID = 'zs';
const SPLASH_VISIBLE_CLASS = 'zs-on';
const SPLASH_EXIT_CLASS = 'zs-out';
/** Salida sin encabezado al que viajar: el reveal entero se funde. */
const SPLASH_FADE_CLASS = 'zs-gone';
/** Queda en `<html>` desde que el reveal se abre sobre el login: Login.css hace entrar el formulario. */
const SPLASH_REVEALING_CLASS = 'zs-rv';
const LOCKUP_SELECTOR = '.zs-l';
/** El lockup del encabezado del login (LoginStage.jsx). */
const LOGIN_LOCKUP_SELECTOR = `.login-lockup ${LOCKUP_SELECTOR}`;
/** Adelanto de las animaciones del lockup. */
const SPLASH_SKIP_PROPERTY = '--zs-d';
/** Adelanto con el que un lockup aparece ya armado (más largo que cualquier entrada). */
const LOCKUP_SETTLED_SKIP_MS = 60000;

/** Lo que tarda el símbolo en armarse; no se retira antes (ver los tiempos en `public/index.html`). */
export const SPLASH_INTRO_MS = 850;
/** Duración de la salida (`zs-dock` en `public/index.html`). */
export const SPLASH_EXIT_MS = 450;
/**
 * Si el login está listo antes de que el logo lleve esto armándose, la carga ya terminó: no vale la pena mostrar la
 * animación y se saltea. Con el bundle en caché pasa siempre; el logo es para las cargas lentas.
 */
export const SPLASH_SKIP_MS = 400;
const SPLASH_FADE_EXIT_MS = 300;
/** Tope de espera desde que arranca la app: pasado, se retira aunque nadie haya avisado. */
export const SPLASH_HOLD_MS = 8000;

/**
 * Si el reveal se saltea porque el login llegó antes de que el logo se viera. Se decide una sola vez, la primera
 * que se pregunta (al armar el lockup del login): el lockup y el retiro tienen que coincidir. Sin animación en
 * curso (pestaña en segundo plano) tampoco hay nada que mostrar.
 */
function skipsReveal(state) {
    if (state.skip === undefined) {
        state.skip = state.startedAt == null || window.performance.now() - state.startedAt < SPLASH_SKIP_MS;
    }
    return state.skip;
}

const getSplashState = () => (typeof window === 'undefined' ? null : window.__zafiroSplash || null);
const getSplashElement = () => (typeof document === 'undefined' ? null : document.getElementById(SPLASH_ELEMENT_ID));

function prefersReducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

/** `true` mientras el reveal tapa la pantalla y todavía no empezó a retirarse. */
export function isLoginSplashActive() {
    const element = getSplashElement();
    return Boolean(element && getSplashState()
        && document.documentElement.classList.contains(SPLASH_VISIBLE_CLASS)
        && !element.classList.contains(SPLASH_EXIT_CLASS));
}

/**
 * Cuánto falta para poder retirarlo sin cortar la entrada: nada si no hay animación o si la entrada todavía no
 * empezó (pestaña en segundo plano). `startedAt` es el instante (reloj de `performance.now()`) en que empezó.
 */
export function getSplashRevealDelay({ now, startedAt, reducedMotion = false }) {
    if (reducedMotion || startedAt == null) return 0;
    return Math.max(0, startedAt + SPLASH_INTRO_MS - now);
}

let lockupHtml = null;

/** Guarda el marcado del lockup antes de que el reveal se quite del documento. */
function captureLockup() {
    if (lockupHtml !== null || typeof document === 'undefined') return;
    const lockup = document.querySelector(`#${SPLASH_ELEMENT_ID} ${LOCKUP_SELECTOR}`);
    lockupHtml = lockup ? lockup.outerHTML : '';
}

/**
 * El lockup para el encabezado del login: el mismo marcado del reveal, con sus animaciones en el mismo instante,
 * así el relevo entre uno y otro no se nota. Sin reveal en curso sale ya armado. Calcularlo una sola vez por
 * montaje: cambiar `style` después reiniciaría las animaciones. `html` va vacío si el documento no trae el
 * reveal (tests).
 */
export function getLoginLockup() {
    captureLockup();
    if (!lockupHtml) return { html: '', style: {} };
    // Un reveal que se va a saltear no anima el lockup del login: sale ya armado, como sin reveal.
    const state = isLoginSplashActive() && !skipsReveal(getSplashState()) ? getSplashState() : null;
    const style = {};
    // Con el reveal a la vista y la entrada sin empezar hereda el adelanto de `<html>` y arrancan juntos.
    if (!state) style[SPLASH_SKIP_PROPERTY] = `${-LOCKUP_SETTLED_SKIP_MS}ms`;
    else if (state.startedAt != null) style[SPLASH_SKIP_PROPERTY] = `${Math.round(state.startedAt - window.performance.now())}ms`;
    return { html: lockupHtml, style };
}

/** En qué elemento de su lockup corre una animación y cuál es: igual en el reveal y en su copia. */
function getLockupAnimationKey(lockup, animation) {
    const steps = [];
    for (let node = animation.effect.target; node && node !== lockup; node = node.parentNode) {
        steps.push(Array.prototype.indexOf.call(node.parentNode.children, node));
    }
    return `${steps.join('.')}|${animation.effect.pseudoElement || ''}|${animation.animationName}`;
}

/**
 * Pone las animaciones del lockup del encabezado en el instante exacto de las del reveal, justo antes del
 * relevo. El adelanto de `getLoginLockup` solo es aproximado: se calcula al renderizar, y las animaciones
 * arrancan recién cuando el login termina de montarse. Sin Web Animations API queda ese adelanto.
 */
function syncLoginLockup() {
    const lockup = document.querySelector(LOGIN_LOCKUP_SELECTOR);
    const source = isLoginSplashActive() ? getSplashElement().querySelector(LOCKUP_SELECTOR) : null;
    if (!lockup || !source || typeof lockup.getAnimations !== 'function') return;

    const delayOf = (animation) => animation.effect.getComputedTiming().delay;
    const running = new Map(source.getAnimations({ subtree: true }).map((animation) => [getLockupAnimationKey(source, animation), animation]));
    lockup.getAnimations({ subtree: true }).forEach((animation) => {
        const match = running.get(getLockupAnimationKey(lockup, animation));
        try {
            // Las que el reveal ya no tiene son las que terminaron sin dejar rastro (destello, anillos).
            if (!match) animation.finish();
            // Mismo origen en la línea de tiempo, corrido lo que difieren los retardos.
            else if (match.startTime != null) animation.startTime = match.startTime + delayOf(match) - delayOf(animation);
        } catch { /* animación que no admite el ajuste: queda con el adelanto de CSS */ }
    });
}

function removeSplash() {
    const state = getSplashState();
    const element = getSplashElement();
    if (element) element.remove();
    // El adelanto del lockup del encabezado queda: quitarlo reiniciaría sus animaciones.
    document.documentElement.classList.remove(SPLASH_VISIBLE_CLASS);
    delete window.__zafiroSplash;
    state?.resolveRemoved?.();
}

/** Deja medido el viaje del lockup hasta su copia en el encabezado del login. `false` si no hay encabezado debajo. */
function dockLockup(element) {
    const from = element.querySelector(LOCKUP_SELECTOR)?.getBoundingClientRect();
    const to = document.querySelector(LOGIN_LOCKUP_SELECTOR)?.getBoundingClientRect();
    if (!from?.width || !to?.width) return false;
    element.style.setProperty('--zs-tx', `${to.left - from.left}px`);
    element.style.setProperty('--zs-ty', `${to.top - from.top}px`);
    element.style.setProperty('--zs-ts', String(to.width / from.width));
    return true;
}

function startExit() {
    const state = getSplashState();
    const element = getSplashElement();
    if (!state || !element) return;
    const docked = !prefersReducedMotion() && dockLockup(element);
    if (docked) syncLoginLockup();
    element.classList.add(SPLASH_EXIT_CLASS);
    if (docked) document.documentElement.classList.add(SPLASH_REVEALING_CLASS);
    else element.classList.add(SPLASH_FADE_CLASS);
    window.setTimeout(removeSplash, (docked ? SPLASH_EXIT_MS : SPLASH_FADE_EXIT_MS) + 80);
}

/** Resuelve cuando el navegador pintó lo que React acaba de montar (o enseguida, si no pinta). */
function afterNextPaint() {
    return new Promise((resolve) => {
        const fallback = window.setTimeout(resolve, 120);
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => { window.clearTimeout(fallback); resolve(); }));
    });
}

/**
 * La app toma el relevo del arranque en línea. Si nadie llama a `revealLoginSplash` en `maxMs` (pantalla que
 * nunca termina de cargar), se retira igual.
 */
export function holdLoginSplash(maxMs = SPLASH_HOLD_MS) {
    captureLockup();
    if (!isLoginSplashActive()) {
        // En las cargas donde no se muestra queda oculto en el DOM: no hace falta.
        if (!getSplashState()) getSplashElement()?.remove();
        return;
    }
    const state = getSplashState();
    state.held = true;
    state.holdTimer = window.setTimeout(() => revealLoginSplash(), maxMs);
}

/**
 * Retira el reveal: espera a que pinte el login, deja terminar la entrada y corre la salida. Idempotente.
 * Resuelve cuando el reveal ya no está (enseguida si no se mostraba).
 */
export function revealLoginSplash() {
    const state = getSplashState();
    if (state?.revealing) return state.removed;
    if (!isLoginSplashActive()) return Promise.resolve();
    if (skipsReveal(state)) {
        // La carga ya terminó: el login está donde tiene que estar y el reveal se quita sin animación.
        window.clearTimeout(state.holdTimer);
        removeSplash();
        return Promise.resolve();
    }
    state.revealing = true;
    state.removed = new Promise((resolve) => { state.resolveRemoved = resolve; });
    state.held = true;
    window.clearTimeout(state.holdTimer);
    afterNextPaint().then(() => {
        const delay = getSplashRevealDelay({ now: window.performance.now(), startedAt: state.startedAt, reducedMotion: prefersReducedMotion() });
        window.setTimeout(startExit, delay);
    });
    return state.removed;
}
